import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { friendlyMessage } from '../lib/errors.ts';
import { cardsSummary, comparePeople, describePerson } from '../lib/people.ts';
import type { LinkCandidate } from '../lib/types.ts';
import { store, useApp } from '../state/store.ts';
import { Avatar } from '../components/common.tsx';

/** Vínculo con Notion: si no hubo coincidencia por correo, selección manual única. */
export function LinkPage() {
  const { email, online } = useApp();
  const [candidates, setCandidates] = useState<LinkCandidate[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!online) return;
    let active = true;
    api
      .peopleForLink()
      .then((list) => active && setCandidates(list))
      .catch((e: unknown) => active && setError(friendlyMessage(e)));
    return () => {
      active = false;
    };
  }, [online]);

  // Disponibles primero; con nombre en orden alfabético y luego sin nombre.
  const options = useMemo(
    () =>
      (candidates ?? [])
        .map((c) => ({ candidate: c, person: describePerson(c.id, c) }))
        .sort(
          (a, b) =>
            Number(a.candidate.linked) - Number(b.candidate.linked) ||
            comparePeople(a.person, b.person) ||
            b.candidate.total_cards - a.candidate.total_cards,
        ),
    [candidates],
  );
  const someUnnamed = options.some((o) => !o.person.named);

  async function confirm() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      store.setProfile(await api.linkManual(selected));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <div className="auth-card auth-card-wide">
        <h1>Vincula tu cuenta con Notion</h1>
        <p>
          No encontramos en Notion una persona con el correo <strong>{email}</strong>. Elige quién
          eres en Notion. <strong>Esta selección se hace una sola vez</strong>; para cambiarla habrá
          que pedirlo a un administrador.
        </p>
        {someUnnamed && (
          <p className="muted small">
            Notion no entrega el nombre de algunas personas (por ejemplo, quienes ya no están en el
            workspace). Aparecen como «Sin nombre» con el final de su id: reconócete por las
            tarjetas en que figuras.
          </p>
        )}
        {!online && (
          <p className="banner banner-offline">Necesitas conexión para vincular tu cuenta.</p>
        )}
        <fieldset className="people-list">
          <legend>Personas en Notion</legend>
          {online && candidates === null && !error && <p className="muted">Cargando personas…</p>}
          {candidates?.length === 0 && (
            <p className="muted">Aún no hay personas sincronizadas desde Notion.</p>
          )}
          {options.map(({ candidate: c, person }) => (
            <label
              key={c.id}
              className={`person-option${c.linked ? ' person-taken' : ''}${person.named ? '' : ' person-unnamed'}`}
            >
              <input
                type="radio"
                name="notion-user"
                value={c.id}
                checked={selected === c.id}
                disabled={c.linked}
                onChange={() => setSelected(c.id)}
              />
              <Avatar person={person} decorative />
              <span className="person-text">
                <span>
                  <span className="person-name">{person.label}</span>
                  {c.email && <span className="muted small"> · {c.email}</span>}
                </span>
                <span className="muted small">
                  {c.linked ? 'Ya vinculada a otra cuenta de Tablero' : cardsSummary(c)}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="row">
          <button
            type="button"
            className="button button-primary"
            disabled={!selected || busy || !online}
            onClick={() => void confirm()}
          >
            Confirmar vínculo
          </button>
          <button type="button" className="link-button" onClick={() => void store.signOut()}>
            Salir
          </button>
        </div>
      </div>
    </main>
  );
}
